import { clientConfig } from '@/config/clientConfig';
import { JobDetailsModal } from '@/domains/calendar/components/JobDetailsModal';
import { computeDurationStatus } from '@/domains/calendar/lib/durationStatus';
import type { Job } from '@/domains/jobs/components/JobCard';
import { useDurationChallengeWorkOrder } from '@/domains/jobs/hooks/useWorkOrderActions';
import { MultiSelectAutocomplete } from '@/shared/components/MultiSelectAutocomplete';
import { PageHeader } from '@/shared/components/PageHeader';
import type { ColorTheme } from '@/shared/lib/colorThemes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { addDays, format, startOfDay } from 'date-fns';
import {
  Activity,
  Calendar as CalendarIcon,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Filter,
  GanttChart,
  Globe,
  Layers,
  LayoutGrid,
  MapPin,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  ShieldCheck,
  Tag,
  Truck,
  Wrench,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { exportGanttToPDF, exportGanttToPNG } from '../utils/exportGantt';
import { AIAutoScheduleModal } from './AIAutoScheduleModal';
import { type CalendarFilterValues, CalendarFiltersModal } from './CalendarFiltersModal';
import { PlannerMapView } from './PlannerMapView';
import { GanttView } from './gantt';

export interface CalendarTeam {
  id: string;
  name: string;
  memberCount?: number;
}

interface CalendarViewProps {
  jobs: Job[];
  onUpdateJob: (jobId: string, updatedJob: Partial<Job>) => void;
  onJobClick?: (jobId: string) => void;
  colorTheme?: ColorTheme;
  onNavigateToFieldWorker?: (jobId: string) => void;
  scheduledJobIds?: string[];
  teams?: CalendarTeam[];
  onSearchChange?: (search: string) => void;
}

const DEFAULT_TEAMS: CalendarTeam[] = [
  { id: '1', name: 'Andy Morris' },
  { id: '2', name: 'Jim Smith' },
  { id: '3', name: 'Sarah Johnson' },
  { id: '4', name: 'Mike Davies' },
  { id: '5', name: 'Lisa Parker' },
];

const DAY_VIEW_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

const JOB_TYPES = [
  'Connection - New Service to New Property - Standard',
  'Connection - New Service to New Property - Non-Standard',
  'Connection - New Service to Existing Property - Standard',
  'Connection - New Service to Existing Property - Non-Standard',
  'Connection - Alteration - Standard',
  'Connection - Alteration - Non-Standard',
  'Connection - Disconnection - Standard',
  'Connection - Disconnection - Non-Standard',
  'Built-over Service Relay',
  'Built-over Service Alteration',
  'GSR Service Disconnection',
  'Service Relay Condition',
  'Survey - Connection - New Service to New Property - Standard',
];

const PRIORITIES = [
  { id: 'all', name: 'All Priorities' },
  { id: 'high', name: 'High' },
  { id: 'medium', name: 'Medium' },
  { id: 'low', name: 'Low' },
];

const _CLIENTS = [
  { id: 'all', name: 'All Clients' },
  { id: 'municipal', name: 'Municipal Water Authority' },
  { id: 'industrial', name: 'Industrial Park Services' },
  { id: 'coastal', name: 'Coastal Treatment District' },
  { id: 'metro', name: 'Metro Regional Utilities' },
  { id: 'county', name: 'County Sanitation Dept' },
  { id: 'private', name: 'Private Facilities Group' },
];

interface PermitBadgeGroupProps {
  permitRequired?: boolean;
  reinstatementRequired?: boolean;
  permitStatusName?: string | null;
}

function PermitBadge() {
  return (
    <Badge variant="outline" className="text-orange-600 border-orange-600 gap-1 text-xs">
      <ShieldCheck className="size-3" />
      Permit
    </Badge>
  );
}

function ReinstatementBadge() {
  return (
    <Badge variant="outline" className="text-blue-600 border-blue-600 text-xs">
      Reinstatement
    </Badge>
  );
}

function PermitStatusLabel({ name }: { name: string }) {
  return <span className="text-xs text-muted-foreground line-clamp-1">{name}</span>;
}

function PermitBadgeGroup({
  permitRequired,
  reinstatementRequired,
  permitStatusName,
}: PermitBadgeGroupProps) {
  if (!permitRequired && !reinstatementRequired && !permitStatusName) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
      {permitRequired && <PermitBadge />}
      {reinstatementRequired && <ReinstatementBadge />}
      {permitStatusName && <PermitStatusLabel name={permitStatusName} />}
    </div>
  );
}

interface DraggableJobCardProps {
  job: Job;
  isScheduled?: boolean;
  variant: 'minimal' | 'vibrant' | 'pastel' | 'gradient' | 'professional';
  index?: number;
  onJobClick?: (jobId: string) => void;
}

function DraggableJobCard({
  job,
  isScheduled,
  variant,
  index = 0,
  onJobClick,
}: DraggableJobCardProps) {
  const [{ isDragging }, drag] = useDrag({
    type: 'JOB',
    item: job,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const statusConfig = {
    pending: {
      label: 'Not Started',
      color: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100',
    },
    'in-progress': {
      label: 'In Progress',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    },
    completed: {
      label: 'Completed',
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
    },
    urgent: {
      label: 'Urgent',
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
    },
  };

  const priorityColors = {
    high: 'text-red-600 dark:text-red-400',
    medium: 'text-yellow-600 dark:text-yellow-400',
    low: 'text-blue-600 dark:text-blue-400',
  };

  // Variant-specific styles
  const getVariantStyles = () => {
    const isEven = index % 2 === 0;

    switch (variant) {
      case 'vibrant':
        return {
          card: isEven
            ? 'bg-gradient-to-br from-blue-500 to-blue-600 border-blue-600 text-white'
            : 'bg-gradient-to-br from-purple-500 to-purple-600 border-purple-600 text-white',
          title: 'text-white font-semibold',
          text: 'text-white/90',
          icon: 'text-white/80',
          badge: isEven
            ? 'bg-blue-700/40 text-white border-blue-400/30'
            : 'bg-purple-700/40 text-white border-purple-400/30',
        };

      case 'pastel':
        return {
          card: isEven
            ? 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/40 dark:to-blue-900/40 border-blue-200 dark:border-blue-800'
            : 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/40 dark:to-purple-900/40 border-purple-200 dark:border-purple-800',
          title: isEven
            ? 'text-blue-900 dark:text-blue-100 font-semibold'
            : 'text-purple-900 dark:text-purple-100 font-semibold',
          text: isEven
            ? 'text-blue-700 dark:text-blue-200'
            : 'text-purple-700 dark:text-purple-200',
          icon: isEven
            ? 'text-blue-600 dark:text-blue-300'
            : 'text-purple-600 dark:text-purple-300',
          badge: isEven
            ? 'bg-blue-200/50 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700'
            : 'bg-purple-200/50 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-700',
        };

      case 'gradient':
        return {
          card: isEven
            ? 'bg-gradient-to-r from-cyan-500/90 via-blue-500/90 to-indigo-500/90 border-blue-400 text-white shadow-lg'
            : 'bg-gradient-to-r from-pink-500/90 via-rose-500/90 to-red-500/90 border-rose-400 text-white shadow-lg',
          title: 'text-white font-bold drop-shadow-sm',
          text: 'text-white/95',
          icon: 'text-white/90',
          badge: 'bg-white/20 text-white border-white/30 backdrop-blur-sm',
        };

      case 'professional':
        return {
          card: isEven
            ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-700'
            : 'bg-blue-50 dark:bg-blue-950/30 border-blue-300 dark:border-blue-800',
          title: isEven
            ? 'text-slate-900 dark:text-slate-100 font-semibold'
            : 'text-blue-900 dark:text-blue-100 font-semibold',
          text: isEven ? 'text-slate-700 dark:text-slate-300' : 'text-blue-700 dark:text-blue-300',
          icon: isEven ? 'text-slate-600 dark:text-slate-400' : 'text-blue-600 dark:text-blue-400',
          badge: isEven
            ? 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600'
            : 'bg-blue-200 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700',
        };

      default: // minimal
        return {
          card: 'bg-card border-border',
          title: 'text-foreground font-medium',
          text: 'text-muted-foreground',
          icon: 'text-muted-foreground',
          badge: statusConfig[job.status].color,
        };
    }
  };

  const styles = getVariantStyles();

  // Get status bar color for scheduled jobs
  const getStatusBar = () => {
    if (!isScheduled) return null;

    switch (job.status) {
      case 'in-progress':
        return 'border-l-4 border-l-orange-500 dark:border-l-orange-400';
      case 'completed':
        return 'border-l-4 border-l-green-500 dark:border-l-green-400';
      case 'urgent':
        return 'border-l-4 border-l-red-500 dark:border-l-red-400';
      default:
        return '';
    }
  };

  // Get milestone indicators
  const getMilestones = () => {
    if (!isScheduled || !job.milestones) return null;

    const iconMap: Record<string, { icon: typeof Truck; color: string }> = {
      arrived_on_site: { icon: Truck, color: 'text-blue-600 dark:text-blue-400' },
      arrivedOnSite: { icon: Truck, color: 'text-blue-600 dark:text-blue-400' },
      departed: { icon: CheckCircle, color: 'text-green-600 dark:text-green-400' },
      abort_actioned: { icon: Activity, color: 'text-red-600 dark:text-red-400' },
      abortActioned: { icon: Activity, color: 'text-red-600 dark:text-red-400' },
    };

    const milestoneList = Object.entries(job.milestones)
      .filter(([, value]) => value)
      .map(([key]) => {
        const mapped = iconMap[key];
        const label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .trim()
          .split(' ')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        return {
          icon: mapped?.icon ?? Wrench,
          label,
          color: mapped?.color ?? 'text-purple-600 dark:text-purple-400',
        };
      });

    return milestoneList.length > 0 ? milestoneList : null;
  };

  const statusBar = getStatusBar();
  const milestones = getMilestones();

  return (
    <div
      ref={drag}
      className={`p-3 rounded-lg hover:shadow-md transition-all cursor-move border-2 ${
        isDragging ? 'opacity-50' : ''
      } ${job.isNew ? 'border-orange-500 dark:border-orange-400 shadow-lg shadow-orange-500/20' : ''} ${isScheduled && variant === 'minimal' && !job.isNew ? 'border-blue-500/50' : ''} ${!job.isNew ? statusBar : ''} ${styles.card}`}
      onClick={() => onJobClick?.(job.id)}
    >
      <div className="flex items-start justify-between mb-1">
        <h4 className={`text-sm line-clamp-1 ${styles.title}`}>{job.title}</h4>
        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
          {job.isNew && (
            <Badge className="text-xs bg-orange-600 text-white border-0 animate-pulse">NEW</Badge>
          )}
          <span
            className={`text-xs font-medium ${
              variant === 'minimal' || variant === 'professional'
                ? priorityColors[job.priority]
                : styles.text
            }`}
          >
            {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}
          </span>
        </div>
      </div>

      {job.category && (
        <div className={`flex items-center gap-1.5 text-xs mb-1 ${styles.text}`}>
          <Layers className={`w-3 h-3 ${styles.icon} flex-shrink-0`} />
          <span className="line-clamp-1 font-medium">{job.category}</span>
        </div>
      )}

      <div className="space-y-1">
        {job.location && (
          <div className={`flex items-center gap-1.5 text-xs ${styles.text}`}>
            <MapPin className={`w-3 h-3 ${styles.icon} flex-shrink-0`} />
            <span className="line-clamp-1">{job.location}</span>
          </div>
        )}

        <div className={`flex items-center justify-between text-xs ${styles.text}`}>
          <div className="flex items-center gap-1.5">
            <Clock className={`w-3 h-3 ${styles.icon}`} />
            <span>{job.duration ?? job.estimatedHours}h</span>
          </div>

          {job.client && (
            <div className="flex items-center gap-1.5">
              <Tag className={`w-3 h-3 ${styles.icon}`} />
              <span className="line-clamp-1">{job.client}</span>
            </div>
          )}
        </div>
      </div>

      <PermitBadgeGroup
        permitRequired={job.permitRequired}
        reinstatementRequired={job.reinstatementRequired}
        permitStatusName={job.permitStatusName}
      />

      {milestones && milestones.length > 0 && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/50">
          {milestones.map((milestone, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-1 px-2 py-0.5 rounded ${
                variant === 'professional' ? 'bg-background/50' : 'bg-white/10'
              }`}
              title={milestone.label}
            >
              <milestone.icon className={`w-3 h-3 ${milestone.color}`} />
              <span className={`text-xs ${milestone.color}`}>{milestone.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UnscheduledDropZone({
  children,
  onDrop,
}: { children: React.ReactNode; onDrop: (job: Job) => void }) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'JOB',
    canDrop: (item: Job) => !!item.scheduledDate,
    drop: (item: Job) => {
      if (item.scheduledDate) onDrop(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <div
      ref={drop}
      className={`flex-1 overflow-y-auto p-3 transition-colors ${isOver && canDrop ? 'bg-amber-50 dark:bg-amber-950/20 ring-2 ring-inset ring-amber-400 rounded' : ''}`}
    >
      {isOver && canDrop && (
        <div className="flex items-center justify-center gap-2 mb-3 py-2 px-3 rounded-md bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700">
          <span className="text-xs font-medium text-amber-800 dark:text-amber-200">
            Drop here to unschedule
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

interface CalendarCellProps {
  teamId: string;
  date: string;
  jobs: Job[];
  jobSpans: Map<string, number>;
  onDrop: (job: Job, teamId: string, date: string) => void;
  variant: 'minimal' | 'vibrant' | 'pastel' | 'gradient' | 'professional';
  onJobClick?: (jobId: string) => void;
}

function CalendarCell({
  teamId,
  date,
  jobs,
  jobSpans,
  onDrop,
  variant,
  onJobClick,
}: CalendarCellProps) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const isPast = date < today;

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'JOB',
    canDrop: () => !isPast,
    drop: (item: Job) => onDrop(item, teamId, date),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const isToday = today === date;

  return (
    <div
      ref={drop}
      className={`relative min-h-[100px] p-2 border-r border-b border-border transition-colors ${
        isOver && canDrop ? 'bg-blue-50 dark:bg-blue-950/20' : ''
      } ${isOver && !canDrop ? 'bg-red-50 dark:bg-red-950/20' : ''} ${isPast ? 'bg-muted/30 opacity-60' : ''} ${isToday && !isOver ? 'bg-blue-50/30 dark:bg-blue-950/10' : ''} ${!isPast && !isToday && !isOver ? 'bg-background' : ''}`}
    >
      {isOver && !canDrop && (
        <div className="absolute inset-x-0 top-1 flex justify-center z-20 pointer-events-none">
          <div className="bg-red-600 text-white text-xs font-medium px-3 py-1.5 rounded-md shadow-lg">
            Cannot schedule in the past
          </div>
        </div>
      )}
      <div className="space-y-2">
        {jobs.map((job, index) => {
          const span = jobSpans.get(job.id) ?? 1;
          return (
            <div
              key={job.id}
              className="relative"
              style={
                span > 1
                  ? {
                      width: `calc(100% * ${span} + ${(span - 1) * 1}px)`,
                      maxWidth: `calc(100% * ${span} + ${(span - 1) * 1}px)`,
                      zIndex: 10,
                    }
                  : undefined
              }
            >
              <DraggableJobCard
                job={job}
                isScheduled
                variant={variant}
                index={index}
                onJobClick={onJobClick}
              />
              {span > 1 && (
                <div className="absolute bottom-1.5 right-2 z-20">
                  <span className="text-[10px] font-medium bg-blue-600 text-white px-1.5 py-0.5 rounded">
                    {span} days
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CalendarView({
  jobs,
  onUpdateJob,
  onJobClick,
  colorTheme,
  onNavigateToFieldWorker,
  scheduledJobIds,
  teams,
  onSearchChange,
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [dayView, setDayView] = useState(3);
  const [selectedJobType, setSelectedJobType] = useState('all');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showAIScheduleModal, setShowAIScheduleModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'gantt' | 'map'>('grid');

  // Gantt-specific state
  const [ganttZoomLevel, setGanttZoomLevel] = useState<'hour' | 'day' | 'week' | 'month'>('week');
  const [ganttDaysToShow, setGanttDaysToShow] = useState(14);
  const [isExporting, setIsExporting] = useState(false);
  const [unscheduleConfirm, setUnscheduleConfirm] = useState<Job | null>(null);
  const [schedulePrompt, setSchedulePrompt] = useState<{
    job: Job;
    teamId: string;
    date: string;
  } | null>(null);
  const [scheduleStartTime, setScheduleStartTime] = useState('08:00');
  const [scheduleEndDate, setScheduleEndDate] = useState('');
  const [scheduleEndTime, setScheduleEndTime] = useState('');
  const [filters, setFilters] = useState<CalendarFilterValues>({
    client: 'all',
    area: 'all',
    team: 'all',
    category: 'all',
  });
  const [sidebarWidth, setSidebarWidth] = useState(340);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [ganttViewportWidth, setGanttViewportWidth] = useState(0);
  const isResizing = useRef(false);
  const ganttContainerRef = useRef<HTMLDivElement>(null);

  const durationChallengeMutation = useDurationChallengeWorkOrder();

  const designVariant = colorTheme?.variant || 'professional'; // Fixed to professional variant

  const viewStart = startOfDay(currentDate);
  const weekDays = Array.from({ length: dayView }, (_, i) => addDays(viewStart, i));

  const handleDrop = (job: Job, teamId: string, date: string) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    if (date < today) return;
    setScheduleStartTime('08:00');
    const durationHours = job.duration ?? job.estimatedHours ?? 1;
    const defaultEnd = new Date(`${date}T08:00:00`);
    defaultEnd.setHours(defaultEnd.getHours() + durationHours);
    setScheduleEndDate(format(defaultEnd, 'yyyy-MM-dd'));
    setScheduleEndTime(format(defaultEnd, 'HH:mm'));
    setSchedulePrompt({ job, teamId, date });
  };

  const confirmSchedule = () => {
    if (!schedulePrompt) return;
    const { job: droppedJob, teamId, date } = schedulePrompt;

    const startISO = `${date}T${scheduleStartTime}:00.000Z`;
    const endISO = `${scheduleEndDate}T${scheduleEndTime}:00.000Z`;

    onUpdateJob(droppedJob.id, {
      scheduledDate: date,
      teamId,
      startTime: startISO,
      endTime: endISO,
    });
    setSchedulePrompt(null);
  };

  const handleUnschedule = (job: Job) => {
    setUnscheduleConfirm(job);
  };

  const confirmUnschedule = () => {
    if (!unscheduleConfirm) return;
    onUpdateJob(unscheduleConfirm.id, {
      scheduledDate: undefined,
      teamId: undefined,
    });
    setUnscheduleConfirm(null);
  };

  const handleJobClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowJobDetailsModal(true);
  };

  const clearAllFilters = () => {
    setFilters({
      client: 'all',
      area: 'all',
      team: 'all',
      category: 'all',
    });
  };

  const handleExportPNG = async () => {
    setIsExporting(true);
    try {
      await exportGanttToPNG(
        'gantt-chart-container',
        `gantt-${format(new Date(), 'yyyy-MM-dd')}.png`,
      );
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      await exportGanttToPDF(
        'gantt-chart-container',
        `gantt-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
      );
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Track Gantt viewport width for responsive column sizing
  useEffect(() => {
    const updateGanttWidth = () => {
      if (ganttContainerRef.current) {
        const width = ganttContainerRef.current.offsetWidth;
        setGanttViewportWidth(width);
      }
    };

    // Initial measurement
    updateGanttWidth();

    // Re-measure on window resize
    window.addEventListener('resize', updateGanttWidth);

    // Re-measure when sidebar width changes
    const resizeObserver = new ResizeObserver(updateGanttWidth);
    if (ganttContainerRef.current) {
      resizeObserver.observe(ganttContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateGanttWidth);
      resizeObserver.disconnect();
    };
  }, [sidebarWidth, sidebarCollapsed]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = Math.max(200, Math.min(600, startWidth + (moveEvent.clientX - startX)));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const activeFiltersCount = Object.values(filters).filter(
    (value) => value !== '' && value !== 'all',
  ).length;

  const matchesSearchTerm = (job: Job) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      job.title.toLowerCase().includes(term) ||
      job.description?.toLowerCase().includes(term) ||
      job.location?.toLowerCase().includes(term) ||
      job.id.toLowerCase().includes(term) ||
      job.client?.toLowerCase().includes(term) ||
      job.assignee?.toLowerCase().includes(term)
    );
  };

  const getJobsForCell = (teamId: string, date: string) => {
    return jobs.filter((job) => {
      if (job.teamId !== teamId) return false;
      if (job.scheduledDate !== date) return false;

      if (!matchesSearchTerm(job)) return false;

      if (selectedJobType !== 'all' && job.title !== selectedJobType) return false;
      if (selectedTeams.length > 0 && !selectedTeams.includes(job.teamId ?? '')) return false;
      if (selectedPriority !== 'all' && job.priority !== selectedPriority) return false;

      if (filters.team !== 'all' && job.teamId !== filters.team) return false;
      if (filters.category !== 'all') {
        const hasCategory = job.tags.some((tag) =>
          tag.toLowerCase().includes(filters.category.toLowerCase()),
        );
        if (!hasCategory) return false;
      }

      return true;
    });
  };

  const getJobSpanDays = (job: Job): number => {
    if (!job.scheduledDate) return 1;
    const endDate = job.scheduledEndDate ?? job.scheduledDate;
    if (endDate === job.scheduledDate) return 1;
    const startMs = new Date(job.scheduledDate).getTime();
    const endMs = new Date(endDate).getTime();
    const days = Math.ceil((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
    const startIdx = weekDays.findIndex((d) => format(d, 'yyyy-MM-dd') === job.scheduledDate);
    if (startIdx === -1) return 1;
    return Math.min(days, dayView - startIdx);
  };

  const unscheduledJobs = jobs.filter((job) => !job.scheduledDate);
  const filteredUnscheduledJobs = unscheduledJobs.filter((job) => {
    // Client-side text search only when API search is not wired up
    if (!onSearchChange && searchTerm) {
      const matchesSearch =
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.assignee?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
    }

    if (selectedJobType !== 'all' && job.title !== selectedJobType) return false;
    if (selectedPriority !== 'all' && job.priority !== selectedPriority) return false;

    if (filters.category !== 'all') {
      const hasCategory = job.tags.some((tag) =>
        tag.toLowerCase().includes(filters.category.toLowerCase()),
      );
      if (!hasCategory) return false;
    }

    return true;
  });

  // Mark scheduledJobIds as "new" and sort them to the top
  const sortedUnscheduledJobs = filteredUnscheduledJobs
    .map((job) => ({
      ...job,
      isNew: scheduledJobIds?.includes(job.id) || false,
    }))
    .sort((a, b) => {
      // New jobs come first
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      return 0;
    });

  const scheduledJobsCount = jobs.filter((j) => j.scheduledDate).length;
  const totalHours = jobs
    .filter((j) => j.scheduledDate)
    .reduce((sum, j) => sum + j.estimatedHours, 0);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page Header with Breadcrumbs */}
      <PageHeader
        title="Job Planner"
        subtitle={`${scheduledJobsCount} jobs scheduled • ${unscheduledJobs.length} unscheduled • ${totalHours}h total`}
        icon={<CalendarIcon className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
        breadcrumbs={[{ label: 'Planner', current: true }]}
      />

      {/* Controls Row */}
      <div className="flex items-center justify-between gap-3 mb-4 px-6 flex-shrink-0 flex-wrap">
        {/* Left side - Search, then filter dropdowns */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, location, ID..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (onSearchChange) {
                  clearTimeout(searchDebounceRef.current);
                  searchDebounceRef.current = setTimeout(() => onSearchChange(value), 300);
                }
              }}
              className="pl-9 w-[260px] bg-background border-border text-foreground h-8 text-sm"
            />
          </div>

          <Select value={selectedJobType} onValueChange={setSelectedJobType}>
            <SelectTrigger
              className="w-[280px] bg-background border-border text-foreground"
              size="sm"
            >
              <SelectValue placeholder="All Job Types" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border max-h-[300px]">
              <SelectItem value="all">All Job Types</SelectItem>
              {JOB_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <MultiSelectAutocomplete
            options={(teams ?? DEFAULT_TEAMS).map((t) => ({ value: t.id, label: t.name }))}
            selected={selectedTeams}
            onChange={setSelectedTeams}
            placeholder="All Teams"
            className="w-[240px]"
          />

          <Select value={selectedPriority} onValueChange={setSelectedPriority}>
            <SelectTrigger
              className="w-[160px] bg-background border-border text-foreground"
              size="sm"
            >
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {PRIORITIES.map((priority) => (
                <SelectItem key={priority.id} value={priority.id}>
                  {priority.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Right side - Date Navigation, Day/Zoom View and Filters */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (viewMode === 'gantt') {
                setCurrentDate(addDays(currentDate, -7));
              } else {
                setCurrentDate(addDays(currentDate, -dayView));
              }
            }}
            className="bg-background border-border text-foreground hover:bg-accent"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded">
            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-foreground whitespace-nowrap">
              {viewMode === 'gantt'
                ? `${format(currentDate, 'MMM d')} – ${format(addDays(currentDate, ganttDaysToShow - 1), 'MMM d, yyyy')}`
                : `${format(viewStart, 'MMM d')} – ${format(addDays(viewStart, dayView - 1), 'MMM d, yyyy')}`}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (viewMode === 'gantt') {
                setCurrentDate(addDays(currentDate, 7));
              } else {
                setCurrentDate(addDays(currentDate, dayView));
              }
            }}
            className="bg-background border-border text-foreground hover:bg-accent"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            onClick={() => setCurrentDate(new Date())}
            className="bg-orange-600 hover:bg-orange-700 text-white border-0"
          >
            Today
          </Button>

          {viewMode === 'gantt' ? (
            /* Gantt zoom and days controls */
            <>
              <div className="flex items-center border border-border rounded-md overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setGanttZoomLevel('hour');
                    setGanttDaysToShow(Math.min(ganttDaysToShow, 3)); // Limit to 3 days max in hour view
                  }}
                  className={`rounded-none h-8 px-3 ${ganttZoomLevel === 'hour' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
                >
                  Hour
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setGanttZoomLevel('day')}
                  className={`rounded-none h-8 px-3 ${ganttZoomLevel === 'day' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
                >
                  Day
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setGanttZoomLevel('week')}
                  className={`rounded-none h-8 px-3 ${ganttZoomLevel === 'week' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
                >
                  Week
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setGanttZoomLevel('month')}
                  className={`rounded-none h-8 px-3 ${ganttZoomLevel === 'month' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
                >
                  Month
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setGanttDaysToShow(
                      Math.max(
                        ganttZoomLevel === 'hour' ? 1 : 7,
                        ganttDaysToShow - (ganttZoomLevel === 'hour' ? 1 : 7),
                      ),
                    )
                  }
                  className="w-8 h-8 p-0"
                  disabled={ganttDaysToShow <= (ganttZoomLevel === 'hour' ? 1 : 7)}
                >
                  −
                </Button>
                <span className="text-sm font-medium min-w-[60px] text-center">
                  {ganttDaysToShow} {ganttDaysToShow === 1 ? 'day' : 'days'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setGanttDaysToShow(
                      Math.min(
                        ganttZoomLevel === 'hour' ? 3 : 90,
                        ganttDaysToShow + (ganttZoomLevel === 'hour' ? 1 : 7),
                      ),
                    )
                  }
                  className="w-8 h-8 p-0"
                  disabled={ganttDaysToShow >= (ganttZoomLevel === 'hour' ? 3 : 90)}
                >
                  +
                </Button>
              </div>
            </>
          ) : (
            /* Grid view day selector */
            <Select
              value={dayView.toString()}
              onValueChange={(value) => setDayView(parseInt(value))}
            >
              <SelectTrigger className="w-[140px] bg-background border-border text-foreground h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {DAY_VIEW_OPTIONS.map((days) => (
                  <SelectItem key={days} value={days.toString()}>
                    {days} {days === 1 ? 'day' : 'days'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('grid')}
              className={`rounded-none h-8 px-3 ${viewMode === 'grid' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
              title="Calendar Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('gantt')}
              className={`rounded-none h-8 px-3 ${viewMode === 'gantt' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
              title="Gantt Timeline View"
            >
              <GanttChart className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('map')}
              className={`rounded-none h-8 px-3 ${viewMode === 'map' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
              title="Map View"
            >
              <Globe className="w-4 h-4" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFiltersModal(true)}
            className="bg-background border-border hover:bg-accent"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {activeFiltersCount > 0 && (
              <Badge
                variant="outline"
                className="ml-2 bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
              >
                {activeFiltersCount}
              </Badge>
            )}
          </Button>

          {/* Export buttons - only show in Gantt view */}
          {viewMode === 'gantt' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPNG}
                disabled={isExporting}
                className="bg-background border-border hover:bg-accent"
                title="Export as PNG image"
              >
                <Download className="w-4 h-4 mr-2" />
                PNG
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={isExporting}
                className="bg-background border-border hover:bg-accent"
                title="Export as PDF document"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </>
          )}
        </div>
      </div>

      {/* AI Insights Section */}
      {/* <div className="flex-shrink-0">
        <CalendarAIInsights jobs={jobs} colorTheme={colorTheme} />
      </div> */}

      {/* Main Content */}
      {viewMode === 'map' ? (
        <PlannerMapView
          jobs={[...filteredUnscheduledJobs, ...jobs.filter((j) => j.scheduledDate)].filter((j) => {
            if (!matchesSearchTerm(j)) return false;
            if (selectedJobType !== 'all' && j.title !== selectedJobType) return false;
            if (selectedTeams.length > 0 && j.teamId && !selectedTeams.includes(j.teamId))
              return false;
            if (selectedPriority !== 'all' && j.priority !== selectedPriority) return false;
            return true;
          })}
          onJobClick={onJobClick}
        />
      ) : viewMode === 'gantt' ? (
        <div className="flex-1 flex overflow-hidden px-6">
          {/* Left Panel - Unscheduled Jobs (Same as grid view) */}
          {!sidebarCollapsed ? (
            <>
              <div
                className="flex flex-col bg-card/50 border border-border rounded-lg overflow-hidden flex-shrink-0"
                style={{ width: sidebarWidth }}
              >
                <div className="p-3 border-b border-border bg-card/90">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-foreground text-sm">
                      Unscheduled ({filteredUnscheduledJobs.length})
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setSidebarCollapsed(true)}
                      title="Collapse panel"
                    >
                      <PanelLeftClose className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <UnscheduledDropZone onDrop={handleUnschedule}>
                  <div className="space-y-2">
                    {filteredUnscheduledJobs.map((job, index) => (
                      <DraggableJobCard
                        key={job.id}
                        job={job}
                        variant={designVariant}
                        index={index}
                        onJobClick={handleJobClick}
                      />
                    ))}
                    {filteredUnscheduledJobs.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        {searchTerm || selectedJobType !== 'all' || selectedTeams.length > 0
                          ? 'No unscheduled jobs match your filters'
                          : 'No unscheduled jobs'}
                      </div>
                    )}
                  </div>
                </UnscheduledDropZone>
              </div>
              {/* Resize handle - matching Calendar mode styling */}
              <div
                className="w-2 flex-shrink-0 cursor-col-resize flex items-center justify-center hover:bg-muted/50 transition-colors group"
                onMouseDown={handleResizeStart}
              >
                <div className="w-0.5 h-8 bg-border group-hover:bg-primary/50 rounded-full transition-colors" />
              </div>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-full w-10 flex-shrink-0 rounded-r-none"
              onClick={() => setSidebarCollapsed(false)}
              title="Show unscheduled jobs"
            >
              <PanelLeftOpen className="w-4 h-4" />
            </Button>
          )}

          {/* Gantt Chart View - wrapped for export */}
          <div
            ref={ganttContainerRef}
            id="gantt-chart-container"
            className="flex-1 flex flex-col overflow-hidden"
          >
            <GanttView
              jobs={jobs.filter((job) => {
                // Apply same filters as grid view
                if (!job.scheduledDate) return false;

                if (!matchesSearchTerm(job)) return false;

                if (selectedJobType !== 'all' && job.title !== selectedJobType) return false;
                if (selectedTeams.length > 0 && !selectedTeams.includes(job.teamId ?? ''))
                  return false;
                if (selectedPriority !== 'all' && job.priority !== selectedPriority) return false;

                if (filters.team !== 'all' && job.teamId !== filters.team) return false;
                if (filters.category !== 'all') {
                  const hasCategory = job.tags.some((tag) =>
                    tag.toLowerCase().includes(filters.category.toLowerCase()),
                  );
                  if (!hasCategory) return false;
                }

                return true;
              })}
              teams={(teams ?? DEFAULT_TEAMS).filter((team) => {
                // Filter teams based on selected filters
                if (selectedTeams.length > 0 && !selectedTeams.includes(team.id)) return false;
                if (filters.team !== 'all' && team.id !== filters.team) return false;
                return true;
              })}
              onUpdateJob={onUpdateJob}
              onJobClick={handleJobClick}
              onDrop={handleDrop}
              zoomLevel={ganttZoomLevel}
              daysToShow={ganttDaysToShow}
              rangeStart={startOfDay(currentDate)}
              viewportWidth={ganttViewportWidth}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden px-6">
          {/* Left Panel - Unscheduled Jobs (Resizable + Collapsible) */}
          {!sidebarCollapsed ? (
            <>
              <div
                className="flex flex-col bg-card/50 border border-border rounded-lg overflow-hidden flex-shrink-0"
                style={{ width: sidebarWidth }}
              >
                <div className="p-3 border-b border-border bg-card/90">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-foreground text-sm">
                      Unscheduled ({filteredUnscheduledJobs.length})
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setSidebarCollapsed(true)}
                      title="Collapse panel"
                    >
                      <PanelLeftClose className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <UnscheduledDropZone onDrop={handleUnschedule}>
                  <div className="space-y-2">
                    {sortedUnscheduledJobs.map((job, index) => (
                      <DraggableJobCard
                        key={job.id}
                        job={job}
                        variant={designVariant}
                        index={index}
                        onJobClick={handleJobClick}
                      />
                    ))}
                    {filteredUnscheduledJobs.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">
                          {searchTerm ? 'No jobs found' : 'All jobs scheduled!'}
                        </p>
                      </div>
                    )}
                  </div>
                </UnscheduledDropZone>
              </div>

              {/* Resize Handle */}
              <div
                className="w-2 flex-shrink-0 cursor-col-resize flex items-center justify-center hover:bg-muted/50 transition-colors group"
                onMouseDown={handleResizeStart}
              >
                <div className="w-0.5 h-8 bg-border group-hover:bg-primary/50 rounded-full transition-colors" />
              </div>
            </>
          ) : (
            <div className="flex-shrink-0 mr-2">
              <Button
                variant="outline"
                size="sm"
                className="h-full px-1.5 border-border rounded-lg"
                onClick={() => setSidebarCollapsed(false)}
                title="Show unscheduled jobs"
              >
                <div className="flex flex-col items-center gap-1">
                  <PanelLeftOpen className="w-4 h-4" />
                  <span className="text-xs [writing-mode:vertical-lr] rotate-180">
                    Unscheduled ({filteredUnscheduledJobs.length})
                  </span>
                </div>
              </Button>
            </div>
          )}

          {/* Right Panel - Calendar Grid */}
          <div className="flex-1 flex flex-col bg-card/50 border border-border rounded-lg overflow-hidden">
            <div className="overflow-auto">
              <div className="min-w-[800px]">
                {/* Header Row - Days */}
                <div
                  className="grid border-b border-border sticky top-0 z-20 bg-card"
                  style={{ gridTemplateColumns: `150px repeat(${dayView}, 1fr)` }}
                >
                  <div className="p-3 border-r border-border bg-card sticky left-0 z-10">
                    <span className="text-sm font-medium text-muted-foreground">Team</span>
                  </div>
                  {weekDays.map((day) => {
                    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    return (
                      <div
                        key={day.toISOString()}
                        className="p-3 border-r border-border last:border-r-0 bg-card"
                      >
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground mb-0.5">
                            {format(day, 'EEE')}
                          </div>
                          <div
                            className={`text-sm font-medium ${
                              isToday ? 'text-blue-600 dark:text-blue-400' : 'text-foreground'
                            }`}
                          >
                            {format(day, 'd MMM')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Team Rows */}
                {(teams ?? DEFAULT_TEAMS)
                  .filter((team) => {
                    if (selectedTeams.length > 0 && !selectedTeams.includes(team.id)) return false;
                    if (filters.team !== 'all' && team.id !== filters.team) return false;
                    return true;
                  })
                  .map((team) => (
                    <div
                      key={team.id}
                      className="grid border-b border-border last:border-b-0"
                      style={{ gridTemplateColumns: `150px repeat(${dayView}, 1fr)` }}
                    >
                      <div className="p-3 border-r border-border bg-card/50 flex items-center sticky left-0 z-10">
                        <span className="text-sm font-medium text-foreground">{team.name}</span>
                      </div>
                      {weekDays.map((day) => {
                        const dateStr = format(day, 'yyyy-MM-dd');
                        const cellJobs = getJobsForCell(team.id, dateStr);
                        const spans = new Map<string, number>();
                        for (const j of cellJobs) {
                          spans.set(j.id, getJobSpanDays(j));
                        }
                        return (
                          <CalendarCell
                            key={`${team.id}-${dateStr}`}
                            teamId={team.id}
                            date={dateStr}
                            jobs={cellJobs}
                            jobSpans={spans}
                            onDrop={handleDrop}
                            variant={designVariant}
                            onJobClick={handleJobClick}
                          />
                        );
                      })}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      <CalendarFiltersModal
        open={showFiltersModal}
        onOpenChange={setShowFiltersModal}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={() => {}}
        onClear={clearAllFilters}
      />

      {/* AI Auto Schedule Modal */}
      <AIAutoScheduleModal
        open={showAIScheduleModal}
        onOpenChange={setShowAIScheduleModal}
        jobs={jobs}
        onUpdateJob={onUpdateJob}
      />

      {/* Schedule Time Picker Modal */}
      <Dialog
        open={!!schedulePrompt}
        onOpenChange={(open) => {
          if (!open) setSchedulePrompt(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Schedule Job</DialogTitle>
            <DialogDescription>
              Set the schedule for{' '}
              <span className="font-medium text-foreground">{schedulePrompt?.job.title}</span>
            </DialogDescription>
          </DialogHeader>

          {schedulePrompt && (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-muted/50 border border-border p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start Date</span>
                  <span className="font-medium text-foreground">
                    {new Date(schedulePrompt.date).toLocaleDateString('en-GB', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estimated Duration</span>
                  <span className="font-medium text-foreground">
                    {schedulePrompt.job.duration ?? schedulePrompt.job.estimatedHours ?? 1} hours
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="schedule-start-time"
                  className="text-sm font-medium text-foreground"
                >
                  Start Time
                </label>
                <Input
                  id="schedule-start-time"
                  type="time"
                  value={scheduleStartTime}
                  onChange={(e) => {
                    setScheduleStartTime(e.target.value);
                    const durationHours =
                      schedulePrompt.job.duration ?? schedulePrompt.job.estimatedHours ?? 1;
                    const newEnd = new Date(`${schedulePrompt.date}T${e.target.value}:00`);
                    newEnd.setHours(newEnd.getHours() + durationHours);
                    setScheduleEndDate(format(newEnd, 'yyyy-MM-dd'));
                    setScheduleEndTime(format(newEnd, 'HH:mm'));
                  }}
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label
                    htmlFor="schedule-end-date"
                    className="text-sm font-medium text-foreground"
                  >
                    End Date
                  </label>
                  <Input
                    id="schedule-end-date"
                    type="date"
                    value={scheduleEndDate}
                    min={schedulePrompt.date}
                    onChange={(e) => setScheduleEndDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="schedule-end-time"
                    className="text-sm font-medium text-foreground"
                  >
                    End Time
                  </label>
                  <Input
                    id="schedule-end-time"
                    type="time"
                    value={scheduleEndTime}
                    onChange={(e) => setScheduleEndTime(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              {(() => {
                const durationHours =
                  schedulePrompt.job.duration ?? schedulePrompt.job.estimatedHours ?? 1;
                const startDt = new Date(`${schedulePrompt.date}T${scheduleStartTime}:00`);
                const endDt = new Date(`${scheduleEndDate}T${scheduleEndTime}:00`);
                const minEndDt = new Date(startDt.getTime() + durationHours * 60 * 60 * 1000);
                const { diffHours, isBeforeStart, isBelowDuration, isAboveDuration, isValid } =
                  computeDurationStatus({
                    durationHours,
                    start: startDt,
                    end: endDt,
                    durationChallengeEnabled: clientConfig.features.durationChallenge,
                  });

                let message = '';
                let colorClass =
                  'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800';
                let textClass = 'text-blue-700 dark:text-blue-300';
                let valueClass = 'text-blue-900 dark:text-blue-100';

                if (isBeforeStart) {
                  message = 'Invalid — end must be after start';
                  colorClass = 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800';
                  textClass = 'text-red-700 dark:text-red-300';
                } else if (isBelowDuration) {
                  message = `End is before the ${durationHours}h estimated duration (minimum: ${format(minEndDt, 'HH:mm')}${format(minEndDt, 'yyyy-MM-dd') !== schedulePrompt.date ? ` on ${format(minEndDt, 'd MMM')}` : ''})`;
                  colorClass =
                    'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800';
                  textClass = 'text-amber-700 dark:text-amber-300';
                } else if (isAboveDuration) {
                  message = `Selected duration (${diffHours}h) exceeds the estimated ${durationHours}h — use "Duration Challenge" to ask for confirmation.`;
                  colorClass =
                    'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800';
                  textClass = 'text-orange-700 dark:text-orange-300';
                }

                return (
                  <div className={`rounded-md p-3 text-sm border ${colorClass}`}>
                    {!isValid ? (
                      <p className={textClass}>{message}</p>
                    ) : (
                      <div className="flex justify-between">
                        <span className={textClass}>Total Duration</span>
                        <span className={`font-medium ${valueClass}`}>
                          {diffHours} hours
                          {scheduleEndDate !== schedulePrompt.date && (
                            <span className="ml-1 text-xs text-blue-600 dark:text-blue-400">
                              (spans {Math.ceil(diffHours / 24)} days)
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedulePrompt(null)}>
              Cancel
            </Button>
            {(() => {
              if (!schedulePrompt) return null;
              const durationHours =
                schedulePrompt.job.duration ?? schedulePrompt.job.estimatedHours ?? 1;
              const startDt = new Date(`${schedulePrompt.date}T${scheduleStartTime}:00`);
              const endDt = new Date(`${scheduleEndDate}T${scheduleEndTime}:00`);
              const { diffHours, isBeforeStart, isBelowDuration, isAboveDuration } =
                computeDurationStatus({
                  durationHours,
                  start: startDt,
                  end: endDt,
                  durationChallengeEnabled: clientConfig.features.durationChallenge,
                });

              if (isAboveDuration) {
                return (
                  <Button
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => {
                      durationChallengeMutation.mutate({
                        jobId: parseInt(schedulePrompt.job.id),
                        diffHours,
                        windowStartDate: undefined,
                        windowEndDate: undefined,
                      });
                      setSchedulePrompt(null);
                    }}
                  >
                    Duration Challenge
                  </Button>
                );
              }

              return (
                <Button
                  className="text-white"
                  style={{ backgroundColor: '#E84A2B' }}
                  onClick={confirmSchedule}
                  disabled={isBeforeStart || isBelowDuration}
                >
                  Schedule
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unschedule Confirmation Modal */}
      <Dialog
        open={!!unscheduleConfirm}
        onOpenChange={(open) => {
          if (!open) setUnscheduleConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Unschedule Job</DialogTitle>
            <DialogDescription>
              Are you sure you want to unschedule{' '}
              <span className="font-medium text-foreground">{unscheduleConfirm?.title}</span>? It
              will be moved back to the unscheduled jobs list.
            </DialogDescription>
          </DialogHeader>
          {unscheduleConfirm?.scheduledDate && (
            <div className="rounded-md bg-muted/50 border border-border p-3 text-sm text-muted-foreground">
              Currently scheduled for{' '}
              <span className="font-medium text-foreground">
                {new Date(unscheduleConfirm.scheduledDate).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnscheduleConfirm(null)}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmUnschedule}
            >
              Unschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Details Modal */}
      <JobDetailsModal
        open={showJobDetailsModal}
        onOpenChange={setShowJobDetailsModal}
        job={jobs.find((j) => j.id === selectedJobId) || null}
        onViewFullDetails={(jobId) => {
          // Close the modal and trigger the full details view
          setShowJobDetailsModal(false);
          if (onJobClick) {
            onJobClick(jobId);
          }
        }}
        onViewFieldWorker={(jobId) => {
          // Navigate to field worker mobile view
          setShowJobDetailsModal(false);
          if (onNavigateToFieldWorker) {
            onNavigateToFieldWorker(jobId);
          } else {
            window.location.href = `/field-worker?jobId=${jobId}`;
          }
        }}
      />
    </div>
  );
}
